import dayjs from 'dayjs';
import FilterForm from '../../../components/forms/FilterForm';
import { Button, DatePicker, Form, Input, Select, Space, Table } from 'antd';
import { CanAccess, CrudFilters, HttpError, useNotification } from '@refinedev/core';
import { ICoupon } from 'interfaces/coupons';
import { IOrder } from 'interfaces/orders';
import { ListLayout } from '../../../components/layout/layout/ListLayout';
import { NotAllowed } from '../../../components/NotAllowed';
import {
  ExportButton,
  ShowButton,
  TagField,
  TextField,
  useTable
} from '@refinedev/antd';
import { formatDate } from 'services/date';
import { formatMoney } from 'services/money';
import { tagColorByStatus } from '../../../utils/statusColorMapping';
import { translateStatus } from 'services/orders';
import { useCorporateReportExport } from '../../corporate_reports/hooks/useCorporateReportExport';
import { normalizeFilterText } from '../../corporate_reports/utils/filters';
import { createOrdersReport } from 'services/orders-report';
import { useCallback, useState } from 'react';
import { StudentInfoCell } from '../../corporate_reports/components/StudentInfoCell';
import { formatCPF } from '../../../utils/document-formatters';

interface IFilter {
  orderNumber: string;
  byEmail: string;
  byCpf: string;
  byPhone: string;
  byProduct: string;
  byStatus: string;
  fromAgent: boolean;
  byCouponCode: string;
  byRangeDate: [dayjs.Dayjs, dayjs.Dayjs];
}

const hasAnyFilter = (values: IFilter) => {
  return !!(
    values.orderNumber ||
    values.byEmail ||
    values.byCpf ||
    values.byPhone ||
    values.byProduct ||
    values.byStatus ||
    values.byCouponCode ||
    (values.byRangeDate?.[0] && values.byRangeDate?.[1]) ||
    (values.fromAgent !== undefined && values.fromAgent !== null)
  );
};

export const OrderList = () => {
  const [hasSearched, setHasSearched] = useState(false);
  const notification = useNotification();

  const { tableProps, searchFormProps } = useTable<IOrder, HttpError, IFilter>({
    onSearch: params => {
      setHasSearched(hasAnyFilter(params));

      const filters: CrudFilters = [];
      const {
        byEmail, byCpf, byPhone, byProduct, fromAgent, byStatus,
        orderNumber, byCouponCode, byRangeDate
      } = params;

      filters.push(
        {
          field: 'filter[by_order_number]',
          operator: 'eq',
          value: orderNumber
        },
        {
          field: 'filter[by_email]',
          operator: 'eq',
          value: byEmail
        },
        {
          field: 'filter[by_cpf]',
          operator: 'eq',
          value: byCpf?.replace(/\D/g, '')
        },
        {
          field: 'filter[by_phone]',
          operator: 'eq',
          value: byPhone?.replace(/\D/g, '')
        },
        {
          field: 'filter[by_product_name]',
          operator: 'eq',
          value: byProduct
        },
        {
          field: 'filter[from_agent]',
          operator: 'eq',
          value: fromAgent
        },
        {
          field: 'filter[by_status]',
          operator: 'eq',
          value: byStatus
        },
        {
          field: 'filter[by_coupon_code]',
          operator: 'eq',
          value: byCouponCode
        },
        {
          field: 'filter[by_range_date][]',
          operator: 'between',
          value: [
            byRangeDate?.[0]?.format('YYYY-MM-DD'),
            byRangeDate?.[1]?.format('YYYY-MM-DD')
          ]
        }
      );

      return filters;
    }
  });

  const createReport = useCallback(async (formValues: IFilter) => {
    const inputs: Record<string, any> = {};
    const orderNumber = normalizeFilterText(formValues.orderNumber);
    const email = normalizeFilterText(formValues.byEmail);
    const product = normalizeFilterText(formValues.byProduct);
    const couponCode = normalizeFilterText(formValues.byCouponCode);

    if (orderNumber) inputs.by_order_number = orderNumber;
    if (email) inputs.by_user_email = email;
    if (formValues.byCpf) inputs.by_cpf = formValues.byCpf.replace(/\D/g, '');
    if (formValues.byPhone)
      inputs.by_phone = formValues.byPhone.replace(/\D/g, '');
    if (product) inputs.by_product_name = product;
    if (formValues.byStatus) inputs.by_status = formValues.byStatus;
    if (formValues.fromAgent !== undefined && formValues.fromAgent !== null)
      inputs.from_agent = formValues.fromAgent;
    if (couponCode) inputs.by_coupon_code = couponCode;
    if (formValues.byRangeDate?.[0] && formValues.byRangeDate?.[1])
      inputs.by_range_date = [
        formValues.byRangeDate[0].format('YYYY-MM-DD'),
        formValues.byRangeDate[1].format('YYYY-MM-DD')
      ];

    return createOrdersReport(inputs);
  }, []);

  const { reportId, handleExportReport } = useCorporateReportExport<IFilter>({
    storageKey: 'orders_report_id',
    searchFormProps,
    notification,
    createReport
  });

  return (
    <CanAccess fallback={<NotAllowed />}>
      <ListLayout
        title="Pedidos"
        headerActions={
          <ExportButton
            onClick={handleExportReport}
            disabled={!!reportId || !hasSearched || !tableProps.dataSource?.length}
            loading={!!reportId}
          >
            {!!reportId ? 'Relatório está sendo gerado' : 'Exportar'}
          </ExportButton>
        }
      >
        <FilterForm searchFormProps={searchFormProps}>
          <Form.Item name="orderNumber">
            <Input placeholder="Número do pedido" />
          </Form.Item>

          <Form.Item name="byEmail">
            <Input placeholder="E-mail do aluno" />
          </Form.Item>

          <Form.Item name="byCpf">
            <Input placeholder="CPF (somente números)" />
          </Form.Item>
          <Form.Item name="byPhone">
            <Input placeholder="Telefone ou DDD" />
          </Form.Item>
          <Form.Item name="byProduct">
            <Input placeholder="Nome do produto" />
          </Form.Item>
          <Form.Item name="byStatus">
            <Select placeholder="Status">
              <Select.Option key="initial">Pagamento não recebido</Select.Option>
              <Select.Option key="created">Pedido criado</Select.Option>
              <Select.Option key="approved">Pedido aprovado</Select.Option>
              <Select.Option key="returned">Pedido devolvido</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="byCouponCode">
            <Input placeholder="Código do cupom" />
          </Form.Item>
          <Form.Item name="byRangeDate">
            <DatePicker.RangePicker format="DD/MM/YYYY" />
          </Form.Item>
          <Form.Item name="fromAgent">
            <Select placeholder="Tipo de pedido">
              <Select.Option key="true">Manual (Gerencial)</Select.Option>
              <Select.Option key="false">Vitrine</Select.Option>
            </Select>
          </Form.Item>
        </FilterForm>
        <Table {...tableProps} rowKey="id">
          <Table.Column
            dataIndex="order_number"
            key="order_number"
            title="Número"
            render={value => <TextField value={value} />}
          />

          <Table.Column
            dataIndex="user"
            key="user"
            title="Informações do aluno"
            className="min-w-[180px] max-w-[250px]"
            render={user => (
              <StudentInfoCell
                userName={user?.full_name_or_name}
                userEmail={user?.email}
              />
            )}
          />
          <Table.Column
            dataIndex="user"
            key="user_document"
            title="CPF"
            width={140}
            className="whitespace-nowrap"
            render={user => (
              <TextField
                value={formatCPF(user?.personal_info?.document_info ?? '')}
              />
            )}
          />
          <Table.Column
            dataIndex="user"
            key="user_phone"
            title="Telefone"
            width={150}
            className="whitespace-nowrap"
            render={user => (
              <TextField value={user?.personal_info?.phone_number || '-'} />
            )}
          />
          <Table.Column
            dataIndex="status"
            key="status"
            title="Status"
            width={150}
            render={value => (
              <TagField
                color={tagColorByStatus(value)}
                value={translateStatus(value)}
              />
            )}
          />
          <Table.Column
            dataIndex="items"
            key="items"
            title="Produtos"
            render={value => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              return value.map((item: any, idx: number) => (
                <TagField
                  style={{ border: 'none' }}
                  color="purple"
                  key={`items=${idx}`}
                  value={item.name}
                />
              ));
            }}
          />
          <Table.Column
            dataIndex="amount"
            key="amount"
            title="Valor"
            render={amount => <TextField value={formatMoney(amount)} />}
          />
          <Table.Column
            dataIndex="coupon"
            key="coupon"
            title="Cupom"
            render={(coupon: ICoupon) => (
              <Button href={`/coupons/edit/${coupon?.id}`} target="_blank">
                {coupon?.code}
              </Button>
            )}
          />
          <Table.Column
            dataIndex="created_at"
            key="created_at"
            title="Data de criação"
            render={value => <TextField value={formatDate(value, 'DD/MM/YYYY HH:mm')} />}
          />
          <Table.Column<IOrder>
            title="Ações"
            dataIndex="actions"
            align="center"
            width={80}
            render={(_, record) => {
              return (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                </Space>
              );
            }}
          />
        </Table>
      </ListLayout>
    </CanAccess>
  );
};
